import { AppData, Lightning } from '@lightningjs/sdk';
import { getImageTextureObj } from 'support/generalUtils';
import { translate } from 'support/translate';
import { RoutePath } from 'config/routes';
import FavoriteChannelTile from 'components/common/tiles/FavoriteChannelTile';
import { TileParentContext } from 'components/common/tiles/Tile';
import {
  buildFavoriteChannelsLaneItem,
  getFavoriteChannelLaneItems,
} from 'support/personaUtils';
import AbstractDetailsPage, {
  AbstractDetailsPageTemplateSpec,
} from './AbstractDetailsPage';
import { ChannelLaneItem } from 'services/cwData';
import { SelectableTileType } from 'components/common/tiles/SelectableTile';
import { SelectableTile } from 'components/common/tiles/SelectableTile';
import { Channel } from 'types/api/media';
import { reportMyStuffAddRemove } from 'services/analytics/reportingServicePage';
import { PageId } from 'types/pageId';
import { MultipleSeriesNameTypes } from 'services/analytics/MParticle';
import { StaticViewContexts } from 'types/analytics';

const CHANNEL_TILE_COLUMNS = 6;
const CHANNEL_TILE_SPACING = 20;

const BOOKMARK_ICON_WIDTH = 48;
const BOOKMARK_ICON_HEIGHT = 64;

const FAVOURITE_CHANNELS_ICON = getImageTextureObj(
  'static/images/bookmark-icon.svg',
  BOOKMARK_ICON_WIDTH,
  BOOKMARK_ICON_HEIGHT,
);

export interface MyChannelsPageTemplateSpec
  extends AbstractDetailsPageTemplateSpec {
  pageData: {
    favoriteChannelLaneItems: Channel[];
  };
}

export default class MyChannelsPage
  extends AbstractDetailsPage<MyChannelsPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<MyChannelsPageTemplateSpec>
{
  protected override _pageId = PageId.FAVORITE_CHANNELS;
  protected override _viewContext =
    StaticViewContexts.MY_STUFF_FAVORITE_CHANNELS;

  override get announce() {
    const favoriteChannels = this.loginConfig?.favorite_channels;
    return this.items && this.items.length > 0
      ? translate('myStuff.favoriteChannels')
      : favoriteChannels?.title ?? '';
  }

  override $remove() {
    super.$remove();
    const numTilesSelected = this._ItemsList.items.filter(
      item => !!(item as SelectableTileType).isSelected,
    ).length;

    this.widgets.removeconfirmationmodal.update({
      numberOfItems: numTilesSelected,
      detailsTitle: translate('myStuff.favoriteChannels'),
    });
    AppData?.modalService.open('RemoveConfirmationModal');
  }

  override async $removeItems() {
    super.$removeItems();

    const channelIdsList = this._ItemsList.items
      .filter(item => !!(item as SelectableTileType).isSelected)
      .map(item => (item.data as Channel).id);

    if (channelIdsList && channelIdsList.length > 0) {
      await this.removeChannels(channelIdsList);
      this.reportRemoveChannels(channelIdsList);
    }

    this.loaded = false;
    await this.fetchItems();
    this.setupFavoriteChannelsGrid();

    this.$toggleEdit();
  }

  override $dismissModal() {
    super.$dismissModal();
  }

  override _onDataProvided() {
    this.loginConfig = AppData?.config?.login.logged_in;
    this.items = this.pageData?.favoriteChannelLaneItems ?? [];
    this.setupFavoriteChannelsGrid();
    this.setupRemoveConfirmationModal();
  }

  private async fetchItems() {
    const favoriteChannelLaneItems = await getFavoriteChannelLaneItems();
    this.items = favoriteChannelLaneItems ?? [];
  }

  private setupFavoriteChannelsGrid() {
    this.loaded = false;

    const favoriteChannelsText = this.loginConfig?.favorite_channels;

    const hasFavoriteChannels = this.items && this.items.length > 0;

    if (hasFavoriteChannels) {
      this.showEmptyGridSection = false;
      this.showEditButton = true;
      this.title = translate('myStuff.favoriteChannels');
      this.subtitle = '';
      const gridItems = this.buildGridTiles(this.items as ChannelLaneItem[]);

      this._ItemsList.patch({
        columns: CHANNEL_TILE_COLUMNS,
        spacing: CHANNEL_TILE_SPACING,
        crossSpacing: CHANNEL_TILE_SPACING,
        listType: FavoriteChannelTile,
        items: gridItems,
      });
    } else if (favoriteChannelsText) {
      this.showEditButton = false;
      this._ItemsList.items = [];

      this._EmptySection.patch({
        icon: FAVOURITE_CHANNELS_ICON,
        title: favoriteChannelsText.title,
        description: favoriteChannelsText.description,
        buttonLabel: favoriteChannelsText.cta_button_text,
        action: RoutePath.EPG,
      });
      this.showEmptyGridSection = true;
    }
    this.loaded = true;
  }

  private buildGridTiles(channels: ChannelLaneItem[]) {
    return channels.map((item, index) => {
      const parentContext: TileParentContext = {
        crossIndex: index,
        mainIndex: 0,
        rowTitle: '',
        isTivoSource: false,
      };

      const { title, slug } = item;

      const laneItem = buildFavoriteChannelsLaneItem({
        channel_id: slug,
        content_title: title,
        content_type: 'LiveChannel',
        last_updated: new Date().getSeconds(),
      });

      return {
        type: SelectableTile(FavoriteChannelTile),
        data: laneItem,
        imageUrl: laneItem.images.homeThumbnail,
        notifyWhenFullyVisible: true,
        parentContext: parentContext,
      };
    });
  }

  private reportRemoveChannels(channelList: string[]) {
    if (!channelList || channelList.length <= 0) return;

    let type: MultipleSeriesNameTypes | string;

    if (channelList.length === this._ItemsList.items.length) {
      type = 'Select All';
    } else if (channelList.length === 1) {
      const channel = this._ItemsList.items.find(
        item => (item.data as Channel).id === channelList[0],
      );
      const channelData = channel?.data as Channel;
      type = channelData.title ?? '';
    } else {
      type = 'Multiple';
    }

    reportMyStuffAddRemove({
      actionType: 'Remove',
      type,
      pageId: this.pageId,
      rowName: 'Channels',
    });
  }

  private async removeChannels(channelIDList: string[]) {
    await AppData?.personaService?.removeFromFavoriteChannels(channelIDList);
  }
}
